import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoogleLogin } from "@react-oauth/google";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import signupImg from "@/assets/furniture-categories.jpg";

export default function Signup() {
  const nav = useNavigate();
  const { signup, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validate = () => {
    const newErrors = {};
    if (!businessName.trim()) newErrors.businessName = "Business name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(email)) newErrors.email = "Invalid email format";
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6) newErrors.password = "Password must be at least 6 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const user = await signup(email, password, businessName);
      toast.success(`Welcome to NEIV, ${user.business_name}! Check your inbox to verify your email.`);
      nav("/verify-email-sent", { replace: true, state: { email } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.msg) {
        toast.error(detail.msg);
      } else {
        toast.error("Signup failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onGoogleSuccess = async (credentialResponse) => {
    setBusy(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        throw new Error("Backend URL not configured");
      }
      const response = await fetch(`${backendUrl}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credentialResponse.credential }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Google signup failed");
      
      localStorage.setItem("neivai_token", data.token);
      localStorage.setItem("neivai_user", JSON.stringify(data.user));
      setUser(data.user);
      
      toast.success(`Welcome to NEIV, ${data.user.business_name || data.user.email}!`);
      const roleHome = data.user.role === "salesperson" ? "/sales" : "/dashboard";
      nav(roleHome, { replace: true });
    } catch (err) {
      toast.error(err.message || "Google signup failed");
      setBusy(false);
    }
  };

  const onGoogleError = () => {
    toast.error("Google signup failed");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden md:block relative">
        <img src={signupImg} alt="Interior" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1A1C1E]/50 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <span className="overline text-white/80">Retailer onboarding</span>
          <h2 className="mt-4 text-4xl font-display font-medium leading-tight">Every store, a design studio.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-16">
        <div className="w-full max-w-md">
          <Logo size="md" />
          <h1 className="mt-12 text-4xl font-display font-semibold tracking-tight">Create your account</h1>
          <p className="mt-2 text-[#5C5F62]">10 free render credits. No card required.</p>
          
          <form className="mt-10 space-y-5" onSubmit={onSubmit}>
            <div>
              <label className="text-sm font-medium">Store / Business name</label>
              <input
                data-testid="signup-business-input"
                required
                value={businessName}
                onChange={(e) => { setBusinessName(e.target.value); if (errors.businessName) setErrors({ ...errors, businessName: "" }); }}
                className={`mt-2 w-full bg-white border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition ${errors.businessName ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                placeholder="Casa Boutique"
              />
              {errors.businessName && <p className="text-xs text-[#B74F4F] mt-1">{errors.businessName}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                data-testid="signup-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({ ...errors, email: "" }); }}
                className={`mt-2 w-full bg-white border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition ${errors.email ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                placeholder="hello@casaboutique.com"
              />
              {errors.email && <p className="text-xs text-[#B74F4F] mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input
                data-testid="signup-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors({ ...errors, password: "" }); }}
                className={`mt-2 w-full bg-white border rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition ${errors.password ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                placeholder="At least 6 characters"
              />
              {errors.password && <p className="text-xs text-[#B74F4F] mt-1">{errors.password}</p>}
            </div>
            <button data-testid="signup-submit-button" type="submit" disabled={busy} className="neivai-btn-primary w-full disabled:opacity-60">
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>

          <div className="mt-8 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E5E5E5]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-[#5C5F62]">Or sign up with</span>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <GoogleLogin
              onSuccess={onGoogleSuccess}
              onError={onGoogleError}
              text="signup_with"
              theme="outline"
              size="large"
            />
          </div>

          <p className="mt-8 text-sm text-[#5C5F62]">
            Already have an account? <Link to="/login" className="text-[#C86B53] font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
